import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { merge, throwError } from 'rxjs';
import { retry, switchMap, take, tap } from 'rxjs/operators';
import { LoadingBarService } from 'src/app/shared/components/loading-bar/loading-bar.service';
import { NotRespondingService } from 'src/app/shared/components/not-responding/not-responding.service';
// Đọc thẳng hằng endpoint, không import service báo lỗi (nó kéo theo ReusableService → HttpClient).
import { MonitoringApi } from '../api/api-monitoring';

const FRONTEND_ERROR_REPORT_URL = MonitoringApi.ReportFrontendError;

const AUTH_URLS = ['refresh-token', 'authenticate', 'logout'];

export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  const notRespondingService = inject(NotRespondingService);
  const loadingBarService = inject(LoadingBarService);

  // Skip retry for auth endpoints — let AuthService.catchError handle errors directly.
  // Retrying here blocks the observable, preventing route guards from resolving.
  if (AUTH_URLS.some((url) => req.url.includes(url))) {
    return next(req);
  }

  // Báo lỗi FE là fire-and-forget: nó chạy ĐÚNG lúc mạng/máy chủ đang hỏng, nên để nó
  // vào nhánh status 0/503 dưới đây là tự mở hộp thoại "Mất kết nối" cho chính lời báo lỗi
  // — chặn cả màn đang dùng (e2e_smoke: 10 ca tạo đơn đỏ vì lớp phủ này).
  if (req.url.includes(FRONTEND_ERROR_REPORT_URL)) {
    return next(req);
  }

  return next(req).pipe(
    retry({
      delay: (err) => {
        // Only intercept network errors (0) and 503 Service Unavailable.
        // Everything else propagates immediately.
        if (err.status !== 0 && err.status !== 503) {
          notRespondingService.check(false, req.url);
          return throwError(() => err);
        }

        // CRITICAL: Clear the loading state for this URL.
        // loadingInterceptor's finalize() will NOT fire while retry holds the stream,
        // so we must manually release it here to unblock the UI.
        loadingBarService._setLoadingStatus(false, req.url);

        // Register the failing URL and open the dialog (no-op if already open).
        notRespondingService.check(true, req.url);

        // If this is a retry that failed again, reset the button so user can retry.
        notRespondingService.setRetryFailed();

        // Wait for user action:
        //   retry → delay emits → new request (dialog stays open, shows "retrying")
        //   cancel → error propagates to errorHandlerInterceptor → component
        return merge(
          notRespondingService.retry,
          notRespondingService.cancel.pipe(
            switchMap(() => {
              notRespondingService.check(false, req.url);
              return throwError(() => err);
            }),
          ),
        ).pipe(take(1));
      },
    }),
    // On success, remove URL from failing map. When map is empty, dialog auto-closes.
    tap((event) => {
      if (event instanceof HttpResponse) {
        notRespondingService.check(false, req.url);
      }
    }),
  );
};
